import { Component, Inject, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {
  RemoteControlDeviceState,
  DeviceStateRegistryService,
} from 'app/modules/remote-control/services/device-state-registry.service';
import { ScreenshotOptions } from '../../../remote-control-ng/mcrc-api';
// import { PromptDialogComponent } from 'src/app/ng-app/shared/components/prompt-dialog/prompt-dialog.component';
import { GlobalEvents } from 'app/core/global-event-constants';
import { GlobalEventBusService, hd, IDeviceModel } from '@nx-fe/services';
import { XsI18nPipe } from '@nx-fe/pipes';
import { IWorkPanelWindow } from 'xsmodules/remote-control/services/work-panel.service';
import { RemoteControlUserPreferencesService } from 'xsmodules/remote-control/remote-control-view/remote-control-user-preferences/remote-control-user-preferences.service';
import { FileSaveParams, PromptDialogComponent } from 'app/shared/components/prompt-dialog/prompt-dialog.component';
import { IRcDeviceContext } from 'app/modules/remote-control-ng/context/deviceContext';
import { RcContext } from 'app/modules/remote-control-ng/context/rcContext';
import { CrossWindowEvents, CrossWindowProxy } from 'app/modules/remote-control-ng/crossWindowProxy';

@Component({
  selector: 'app-rc-screenshot',
  templateUrl: './rc-screenshot.component.html',
  styleUrls: ['./rc-screenshot.component.scss'],
})
export class RcScreenshotComponent implements OnInit {
  @Input() popOut = false;
  @Input() standAlone: boolean;
  @Input() rcState: RemoteControlDeviceState;

  private $window: IWorkPanelWindow = window as any;
  constructor(
    private saI18nPipe: XsI18nPipe,
    private dialog: MatDialog,
    private globalEventBusService: GlobalEventBusService,
    private deviceStateRegistry: DeviceStateRegistryService,
    private rcUserPreferencesService: RemoteControlUserPreferencesService
  ) {}

  ngOnInit(): void {}

  public takeScreenshot = () => {
    let options$: Observable<ScreenshotOptions>;
    const attach = !this.standAlone || (this.rcState && this.rcState.poppedOut);

    if (attach === true) {
      // Do not open FileName dialog for attachment
      const options = this.getScreenshotOptions(attach, this.rcState.device);
      RcContext.instance.session.appUI.takeScreenshot(options);
      options$ = of(options);
    } else {
      // open file name dialog
      const dialogParams = this.getScreenshotDialogParams(this.rcState.device);
      dialogParams.placeHolder = this.rcState.device.deviceName;
      const fileSaveParams: hd.FileSaveParams = {
        fileName: dialogParams.placeHolder,
        extension: dialogParams.extension,
      };

      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = true;
      dialogConfig.autoFocus = true;
      dialogConfig.width = '400px';
      dialogConfig.height = '230px';
      dialogConfig.panelClass = 'custom-dialog-scrollable-container';
      dialogConfig.closeOnNavigation = true;

      dialogConfig.data = {
        title: dialogParams.title,
        icon: dialogParams.icon,
        confirmButton: dialogParams.confirmButton,
        dialogParamsTitle: dialogParams.inputTitle,
        extension: dialogParams.extension,
        placeHolder: dialogParams.placeHolder,
        regexFilter: dialogParams.regexFilter,
        unallowedChar: dialogParams.unallowedChar,
        fileParams: fileSaveParams,
      };

      const modalInstance = this.dialog.open(PromptDialogComponent, dialogConfig);
      options$ = modalInstance.afterClosed().pipe(
        map((result: FileSaveParams) => {
          if (result) {
            const options = this.getScreenshotOptions(attach, this.rcState.device);
            options.name = result.fileName;
            RcContext.instance.session.appUI.takeScreenshot(options);
            return options;
          }
        })
      );
    }

    options$.pipe(take(1)).subscribe((options: ScreenshotOptions) => {
      // options are null when the download is cancelled from the prompt dialog
      if (options) {
        if (this.popOut) {
          if (options.fileObject) {
            options.name = options.fileObject.name;
          }
          CrossWindowProxy.postMessage(this.$window.opener, CrossWindowEvents.WORK_PANEL_ATTACH_SCREENSHOT, undefined, options);
        } else {
          this.globalEventBusService.broadcast(GlobalEvents.attachScreenshot, options);
        }
      }
    });
  };

  public getScreenshotDialogParams = (device: IDeviceModel): hd.PromptDialogParams => {
    let deviceName = 'screenshot';
    if (device) {
      deviceName = device.deviceName || 'screenshot';
    }
    const state = this.deviceStateRegistry.getCurrentState();
    const type =
      state && state.screenCapturePrefs && state.screenCapturePrefs.imageFormat
        ? state.screenCapturePrefs.imageFormat
        : this.rcUserPreferencesService.defaultImageFormat();
    const extension = '.' + type.toLowerCase();

    return {
      title: this.saI18nPipe.transform('prompt_dialog_title_download'),
      icon: 'hd-icon-download',
      confirmButton: this.saI18nPipe.transform('prompt_dialog_title_download'),
      inputTitle: this.saI18nPipe.transform('prompt_dialog_screenshot_FileName'),
      extension,
      placeHolder: deviceName,
      regexFilter: /[/*?":|<>\\]/g,
      unallowedChar: '\\ * ? " : | < > /',
    } as hd.PromptDialogParams;
  };

  public getScreenshotOptions = (attach: boolean, device: IDeviceModel): ScreenshotOptions => {
    let deviceName = 'screenshot';
    if (device) {
      deviceName = device.deviceName || 'screenshot';
    }

    const skin = (<IRcDeviceContext>RcContext.instance.deviceContext)?.imageSkin;
    const state = this.deviceStateRegistry.getCurrentState();
    const type =
      state && state.screenCapturePrefs && state.screenCapturePrefs.imageFormat
        ? state.screenCapturePrefs.imageFormat
        : this.rcUserPreferencesService.defaultImageFormat();

    const MAX_FILE_NAME = 255;
    const formattedDateTimeString = this.getFormattedDateTimeString();
    const maxDeviceNameLength = MAX_FILE_NAME - (type.length + formattedDateTimeString.length + 1);
    const tempName =
      (deviceName.length > maxDeviceNameLength ? deviceName.substring(0, maxDeviceNameLength) : deviceName) +
      formattedDateTimeString;

    return {
      name: tempName,
      type,
      quality:
        state && state.screenCapturePrefs && state.screenCapturePrefs.imageQuality
          ? state.screenCapturePrefs.imageQuality.toString()
          : this.rcUserPreferencesService.defaultImageQuality().toString(),
      bpp:
        state && state.screenCapturePrefs && state.screenCapturePrefs.bitDepth
          ? state.screenCapturePrefs.bitDepth.toString()
          : this.rcUserPreferencesService.defaultBitDepth().toString(),
      skin: skin && !skin.emptySkin,
      attachment: attach,
      associatedDeviceName: deviceName,
      device,
    };
  };

  private getFormattedDateTimeString = (): string => {
    const now = new Date();
    const theDay = this.padLeadingZero(now.getDate());
    const theMonth = this.padLeadingZero(now.getMonth() + 1);
    const theYear = now.getFullYear();
    const theHour = this.padLeadingZero(now.getHours());
    const theMinutes = this.padLeadingZero(now.getMinutes());
    const theSeconds = this.padLeadingZero(now.getSeconds());

    const formattedFileNameString = '_' + theYear + theMonth + theDay + '_' + theHour + theMinutes + theSeconds;

    return formattedFileNameString;
  };

  /**
   * PadLeadingZero
   *
   * @param num A numeric value.
   * @param size A numeric value of trailing digits to return.
   * Example:
   *  Input   Output
   *    1      01
   *   12      12
   */
  private padLeadingZero = (num: number, size?: number): string =>
    size ? ('0' + num).slice(-size) : ('0' + num).slice(-2);
}
